import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Plus,
  Search,
  Tag,
  Trash2,
} from 'lucide-react';
import { InventoryStatus, type DraftInventoryInput, type Inventory } from '@ncc/shared';
import { api } from '../lib/api';
import { formatDateTime, formatMoney, listingPrice } from '../lib/format';

const StatusFilter = z.union([InventoryStatus, z.literal('all')]);
type StatusFilter = z.infer<typeof StatusFilter>;

export const InventoryListSearchSchema = z.object({
  q: z.string().optional(),
  status: StatusFilter.optional(),
  brand_id: z.coerce.number().int().optional(),
  type_id: z.coerce.number().int().optional(),
  tag_id: z.coerce.number().int().optional(),
  page: z.coerce.number().int().min(1).optional(),
  page_size: z.coerce.number().int().min(1).max(100).optional(),
  sort: z
    .enum(['newest', 'oldest', 'price_asc', 'price_desc', 'year_desc', 'year_asc'])
    .optional(),
});
export type InventoryListSearch = z.infer<typeof InventoryListSearchSchema>;

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT = 'newest' as const;
const DEFAULT_STATUS: StatusFilter = 'published';
const PUBLIC_CATALOGUE_URL = 'https://www.ncchd.ca/inventory-catalogue';

export function InventoryList() {
  const search = useSearch({ from: '/' }) as InventoryListSearch;
  const navigate = useNavigate({ from: '/' });
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const brandsQ = useQuery({ queryKey: ['brands'], queryFn: api.listBrands });
  const typesQ = useQuery({ queryKey: ['inventory-types'], queryFn: api.listInventoryTypes });
  const tagsQ = useQuery({ queryKey: ['tags'], queryFn: api.listTags });

  // Default to "published" so the most-relevant rows show first; user can
  // pick "all" to see drafts / hidden / sold mixed in.
  const activeStatus: StatusFilter = search.status ?? DEFAULT_STATUS;
  const effective = {
    ...search,
    // Don't send "all" to the API — translate it to "no filter".
    status: activeStatus === 'all' ? undefined : activeStatus,
    page: search.page ?? DEFAULT_PAGE,
    page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
    sort: search.sort ?? DEFAULT_SORT,
  };

  const listQ = useQuery({
    queryKey: ['inventory', effective],
    queryFn: () => api.listInventory(effective),
    placeholderData: (p) => p,
  });

  const bulk = useMutation({
    mutationFn: api.bulk,
    onSuccess: () => {
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  // Duplicate an existing listing into a new draft with a blank Inventory ID and
  // VIN — used when stock has several identical units that each need their own ID.
  const duplicate = useMutation({
    mutationFn: (item: Inventory) => api.saveDraft(cloneToDraft(item)),
    onSuccess: (draft) => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      navigate({ to: '/edit/$id', params: { id: String(draft.id) } });
    },
  });

  const items = listQ.data?.items ?? [];
  const allChecked = items.length > 0 && items.every((i) => selected.has(i.id));

  const update = (patch: Partial<InventoryListSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }) });

  const totalPages = useMemo(() => {
    if (!listQ.data) return 1;
    return Math.max(1, Math.ceil(listQ.data.total / listQ.data.page_size));
  }, [listQ.data]);


  return (
    <>
      <header className="toolbar">
        <h2 style={{ margin: 0 }}>Inventory</h2>
        <a
          href={PUBLIC_CATALOGUE_URL}
          target="_blank"
          rel="noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.85rem' }}
        >
          <ExternalLink size={14} /> View live catalogue
        </a>
        <span className="grow" />
        <Link to="/new">
          <button className="primary">
            <Plus size={16} /> New Inventory
          </button>
        </Link>
      </header>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="toolbar">
          <div style={{ position: 'relative', maxWidth: 360, flex: '1 1 240px' }}>
            <Search
              size={14}
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted)',
              }}
            />
            <input
              placeholder="Search vehicle name, description, ID…"
              defaultValue={search.q ?? ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter') update({ q: (e.target as HTMLInputElement).value });
              }}
              style={{ paddingLeft: 30 }}
            />
          </div>
          <select
            value={activeStatus}
            onChange={(e) => update({ status: e.target.value as StatusFilter })}
            style={{ maxWidth: 160 }}
          >
            <option value="all">All statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="hidden">Hidden</option>
            <option value="sold">Sold</option>
          </select>
          <select
            value={search.brand_id ?? ''}
            onChange={(e) => update({ brand_id: e.target.value ? Number(e.target.value) : undefined })}
            style={{ maxWidth: 200 }}
          >
            <option value="">All brands</option>
            {brandsQ.data?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <select
            value={search.type_id ?? ''}
            onChange={(e) => update({ type_id: e.target.value ? Number(e.target.value) : undefined })}
            style={{ maxWidth: 220 }}
          >
            <option value="">All types</option>
            {typesQ.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <select
            value={search.tag_id ?? ''}
            onChange={(e) => update({ tag_id: e.target.value ? Number(e.target.value) : undefined })}
            style={{ maxWidth: 200 }}
          >
            <option value="">All tags</option>
            {tagsQ.data?.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <select
            value={effective.sort}
            onChange={(e) => update({ sort: e.target.value as InventoryListSearch['sort'] })}
            style={{ maxWidth: 180 }}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="price_desc">Highest price</option>
            <option value="price_asc">Lowest price</option>
            <option value="year_desc">Newest year</option>
            <option value="year_asc">Oldest year</option>
          </select>
        </div>
      </div>

      {selected.size > 0 ? (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="toolbar">
            <strong>{selected.size} selected</strong>
            <button
              onClick={() => bulk.mutate({ action: 'publish', ids: [...selected] })}
              disabled={bulk.isPending}
            >
              <Eye size={14} /> Publish
            </button>
            <button
              onClick={() => bulk.mutate({ action: 'hide', ids: [...selected] })}
              disabled={bulk.isPending}
            >
              <EyeOff size={14} /> Hide
            </button>
            <button
              onClick={() => bulk.mutate({ action: 'mark_sold', ids: [...selected] })}
              disabled={bulk.isPending}
            >
              <Tag size={14} /> Mark sold
            </button>
            <button
              className="danger"
              onClick={() => {
                if (confirm(`Delete ${selected.size} inventory item(s)? This cannot be undone.`)) {
                  bulk.mutate({ action: 'delete', ids: [...selected] });
                }
              }}
              disabled={bulk.isPending}
            >
              <Trash2 size={14} /> Delete
            </button>
            <span className="grow" />
            <button className="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        </div>
      ) : null}

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => {
                    if (e.target.checked) setSelected(new Set(items.map((i) => i.id)));
                    else setSelected(new Set());
                  }}
                />
              </th>
              <th style={{ width: 88 }}>Photo</th>
              <th style={{ width: 90 }}>Status</th>
              <th style={{ width: 96 }}>Condition</th>
              <th className="wrap">Vehicle</th>
              <th style={{ width: 140 }}>Brand</th>
              <th style={{ width: 160 }}>Type</th>
              <th style={{ width: 70, textAlign: 'right' }}>Year</th>
              <th style={{ width: 130, textAlign: 'right' }}>Price</th>
              <th style={{ width: 180 }}>Updated</th>
              <th style={{ width: 56 }}></th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading ? (
              <tr>
                <td colSpan={11} style={{ textAlign: 'center', padding: '2rem' }}>
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={11} style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>
                  No inventory matches the current filters.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={(e) => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(item.id);
                          else next.delete(item.id);
                          return next;
                        });
                      }}
                    />
                  </td>
                  <td>
                    <div
                      className="thumb"
                      style={
                        item.images[0]?.url
                          ? { backgroundImage: `url(${item.images[0].url})` }
                          : undefined
                      }
                      aria-label={item.images[0]?.url ? '' : 'No image'}
                    />
                  </td>
                  <td>
                    <span className={`badge ${item.status}`}>{item.status}</span>
                  </td>
                  <td>
                    {/* Always the stored value, including on classic vehicles.
                        The public site suppresses the Used badge on those
                        (showsUsedBadge in @ncc/shared) because buyers already
                        know the classics are used — but staff are here to check
                        the record, so hiding it would mean a field you can edit
                        and cannot see. Don't "align" this with the widget rule. */}
                    <span className={`badge ${item.condition.toLowerCase()}`}>
                      {item.condition}
                    </span>
                  </td>
                  <td className="wrap">
                    <Link
                      to="/edit/$id"
                      params={{ id: String(item.id) }}
                      style={{ fontWeight: 500, color: 'var(--ink)' }}
                    >
                      {item.year} {item.brand_name ?? ''} {item.vehicle_name}
                    </Link>
                    <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
                      {item.inventory_id}
                      {item.location ? ` · ${item.location}` : ''}
                    </div>
                  </td>
                  <td>{item.brand_name ?? '—'}</td>
                  <td>{item.type_name ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{item.year}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(listingPrice(item))}
                  </td>
                  <td style={{ color: 'var(--muted)' }}>{formatDateTime(item.updated_at)}</td>
                  <td>
                    <button
                      className="ghost"
                      title="Duplicate as a new draft (blank ID & VIN)"
                      aria-label="Duplicate listing"
                      onClick={() => duplicate.mutate(item)}
                      disabled={duplicate.isPending}
                    >
                      <Copy size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="toolbar" style={{ marginTop: '1rem', justifyContent: 'center' }}>
        <button
          onClick={() => update({ page: Math.max(1, (search.page ?? 1) - 1) })}
          disabled={(search.page ?? 1) <= 1}
        >
          <ChevronLeft size={14} /> Prev
        </button>
        <span style={{ alignSelf: 'center', color: 'var(--muted)' }}>
          Page {effective.page} of {totalPages} ({listQ.data?.total ?? 0} total)
        </span>
        <button
          onClick={() => update({ page: Math.min(totalPages, (search.page ?? 1) + 1) })}
          disabled={(search.page ?? 1) >= totalPages}
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    </>
  );
}

// Build a draft payload from an existing listing, dropping the unique identity
// fields (Inventory ID, VIN) and all server-managed fields so the copy starts
// blank where it must. Images are carried over.
function cloneToDraft(item: Inventory): DraftInventoryInput {
  const {
    id: _id,
    slug: _slug,
    inventory_id: _inventoryId,
    vin: _vin,
    status: _status,
    sold_at: _soldAt,
    published_at: _publishedAt,
    created_at: _createdAt,
    updated_at: _updatedAt,
    brand_name: _brandName,
    type_name: _typeName,
    tag_name: _tagName,
    tag_style: _tagStyle,
    images,
    ...rest
  } = item;
  return {
    ...rest,
    status: 'draft',
    images: images.map((im) => ({ url: im.url, image_id: im.image_id, position: im.position })),
  };
}
